import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, CheckCircle, Users, Briefcase, Megaphone, 
  Share2, Layers, Zap, DollarSign, Award, Globe,
  Handshake, TrendingUp, BookOpen, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

// Five Partner Pathways Data
const PATHWAYS = [
  {
    id: 1,
    name: "Referral & Client Partners",
    icon: Users,
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    role: "Introduce organisations to CNESS and assist them through the onboarding process. Suitable for advisors, consultants, and agencies.",
    revenue: [
      "15% share of certified organisation's first-year certification fee",
      "5% recurring share on annual renewals for the first two years"
    ],
    benefits: [
      "Partner resources and training materials",
      "Joint marketing opportunities",
      "Listing in the Partner Directory"
    ]
  },
  {
    id: 2,
    name: "Assessment & Implementation Partners",
    icon: Briefcase,
    color: "#8B5CF6",
    bgColor: "#F5F3FF",
    role: "Guide organisations through readiness assessments, gap analyses, and improvement projects. Typical partners include consulting firms and auditors.",
    revenue: [
      "Set your own project fees for consulting work",
      "20% share of certification fee for supported organisations",
      "10% share of learning modules or toolkits purchased by clients"
    ],
    benefits: [
      "Training on the six-pillar framework",
      "Co-branding on case studies",
      "Implementation toolkit access"
    ]
  },
  {
    id: 3,
    name: "Media & Community Partners",
    icon: Megaphone,
    color: "#10B981",
    bgColor: "#ECFDF5",
    role: "Amplify the visibility of certified organisations through media channels, events, podcasts, or community platforms.",
    revenue: [
      "Revenue from sponsored content and co-hosted events",
      "10% share of subscription fees from organisations joining via your audience"
    ],
    benefits: [
      "Co-branding on events",
      "Priority access to industry insights",
      "Homepage and directory placement"
    ]
  },
  {
    id: 4,
    name: "Affiliate & Influencer Partners",
    icon: Share2,
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    role: "Drive awareness and discovery through blogs, newsletters, and influencer channels.",
    revenue: [
      "Fixed bonus ($200–$500) per certified organisation via affiliate link",
      "Tiered bonus structure (+20% after 10 conversions per quarter)"
    ],
    benefits: [
      "Early access to content and product announcements",
      "Exclusive discount codes for your audience",
      "Performance dashboard access"
    ]
  },
  {
    id: 5,
    name: "Certification & Bundle Partners",
    icon: Layers,
    color: "#EC4899",
    bgColor: "#FDF2F8",
    role: "Integrate CNESS certification into existing ESG software, compliance platforms, or training programs.",
    revenue: [
      "30–40% share of license revenue on bundled packages",
      "API access fees for platform integration",
      "10% shared revenue from aggregated anonymous data insights"
    ],
    benefits: [
      "Co-development opportunities",
      "Technical support and integration assistance",
      "Co-marketing campaigns"
    ]
  }
];

// Partner Benefits
const PARTNER_BENEFITS = [
  { icon: DollarSign, title: "Revenue Opportunities", desc: "Multiple revenue streams tailored to your partner type" },
  { icon: Award, title: "Recognition", desc: "Directory listing and certification as a CNESS partner" },
  { icon: BookOpen, title: "Resources", desc: "Access to training, toolkits, and marketing materials" },
  { icon: Globe, title: "Network", desc: "Connect with conscious businesses worldwide" },
  { icon: TrendingUp, title: "Growth", desc: "Expand your services with CNESS integration" },
  { icon: Handshake, title: "Support", desc: "Dedicated partner success team" }
];

const NewPartnerProgramPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero Section — Brand-aligned (cream/white, not dark) */}
      <section className="py-16 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="partner-program-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Zap className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">CNESS Partner Program</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
              Join the CNESS
              <span className="block text-[#FDB813]">Partner Network</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8">
              Entrepreneurs, consultants, media platforms, creators, and technology partners — 
              discover mutually beneficial revenue opportunities and shared impact through our 
              five partnership pathways.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => navigate('/partner-application')}
                data-testid="partner-program-apply-btn"
                className="w-full sm:w-auto px-8 py-6 text-base bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl shadow-md"
              >
                Become a Partner
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="w-full sm:w-auto px-8 py-6 text-base border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl"
              >
                Schedule a Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Stats */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "5", label: "Partner Pathways" },
              { value: "15-40%", label: "Revenue Shares" },
              { value: "Global", label: "Network Reach" },
              { value: "24/7", label: "Partner Support" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-[#1E3A5F]">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Five Pathways Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              Five Partnership Pathways
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the pathway that best aligns with your expertise and business model.
            </p>
          </div>
          
          <div className="space-y-8">
            {PATHWAYS.map((pathway, index) => (
              <div 
                key={pathway.id}
                className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="grid lg:grid-cols-3 gap-0">
                  {/* Pathway Info */}
                  <div className="p-8 lg:border-r border-gray-100">
                    <div className="flex items-center gap-4 mb-4">
                      <div 
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: pathway.bgColor }}
                      >
                        <pathway.icon className="w-7 h-7" style={{ color: pathway.color }} />
                      </div>
                      <span 
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: pathway.bgColor, color: pathway.color }}
                      >
                        Pathway {pathway.id}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">{pathway.name}</h3>
                    <p className="text-gray-600">{pathway.role}</p>
                  </div>
                  
                  {/* Revenue Model */}
                  <div className="p-8 bg-[#F8FAFC] lg:border-r border-gray-100">
                    <h4 className="font-semibold text-[#1E3A5F] mb-4 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                      Revenue Opportunities
                    </h4>
                    <ul className="space-y-3">
                      {pathway.revenue.map((item, i) => (
                        <li key={i} className="flex items-start text-sm">
                          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0 text-green-500 mt-0.5" />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Benefits */}
                  <div className="p-8">
                    <h4 className="font-semibold text-[#1E3A5F] mb-4 flex items-center">
                      <Star className="w-5 h-5 mr-2 text-[#FDB813]" />
                      Partner Benefits
                    </h4>
                    <ul className="space-y-3">
                      {pathway.benefits.map((item, i) => (
                        <li key={i} className="flex items-start text-sm">
                          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" style={{ color: pathway.color }} />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-gray-500 mt-8 italic">
            Note: Revenue percentages are illustrative and may be customised based on partnership terms.
          </p>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              Why Partner with CNESS?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join a growing network of purpose-driven professionals committed to conscious business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTNER_BENEFITS.map((benefit, index) => (
              <div key={index} className="p-6 bg-[#F8FAFC] rounded-2xl hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#FEF3C7] rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#FDB813]" />
                </div>
                <h4 className="font-semibold text-[#1E3A5F] mb-2">{benefit.title}</h4>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              How to Become a Partner
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Apply", desc: "Complete our partner application form with your details and partnership interests." },
              { step: "02", title: "Review", desc: "Our team reviews your application and schedules an introduction call." },
              { step: "03", title: "Onboard", desc: "Complete partner training and receive access to resources and tools." },
              { step: "04", title: "Launch", desc: "Start referring clients, delivering services, or integrating CNESS into your offerings." }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-16 h-16 bg-[#1E3A5F] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-white">{item.step}</span>
                </div>
                <h4 className="font-semibold text-[#1E3A5F] mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-[#FDB813]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A5F] to-[#0F172A] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Partner with CNESS?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Join a network of conscious business advocates and create meaningful impact 
            while building sustainable revenue streams.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/partner-application')}
              className="w-full sm:w-auto px-8 py-4 text-lg bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold rounded-xl"
            >
              Apply Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="w-full sm:w-auto px-8 py-4 text-lg border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] rounded-xl"
            >
              Talk to Partner Team
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NewPartnerProgramPage;
